'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { detectMimeType } from '../lib/image-detect';
import { calculatePoolSize } from '../lib/pool-size';
import { WorkerPool } from '../lib/worker-pool';
import type { CompressionMode, CompressionProfile, CompressionRequest } from '../types/worker-messages';
export interface CompressionOutput {
  compressedBlob: Blob;
  contentType: string;
  originalWidth: number;
  originalHeight: number;
  compressedWidth: number;
  compressedHeight: number;
  dpiX: number;
  dpiY: number;
}
export interface UseImageCompressionOptions {
  compressionMode?: CompressionMode;
  /** 압축 프로파일. 미지정 시 'default' (기존 배너/상세/스티커 정책). */
  profile?: CompressionProfile;
}
type CompressImageOptions = {
  minImageWidth?: number;
  minImageHeight?: number;
  /**
   * 압축이 끝나기 전에 도착하는 그리드용 썸네일. 워커가 디코드와 치수 검증을
   * 통과했다는 신호이기도 하다. 넘기지 않으면 워커는 썸네일을 만들지 않는다.
   */
  onPreview?: (thumbnail: Blob) => void;
};
export function useImageCompression(t0) {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    compressionMode: t2,
    profile: t3
  } = t1;
  const compressionMode = t2 === undefined ? "reencode-when-resize-only" : t2;
  const profile = t3 === undefined ? "default" : t3;
  const t = useTranslations("imageUpload");
  const compressionPool = useRef(null);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const poolSize = calculatePoolSize();
      compressionPool.current = new WorkerPool(_temp, poolSize);
      return () => {
        compressionPool.current?.terminate();
      };
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] !== compressionMode || $[5] !== profile || $[6] !== t) {
    t6 = async (id, file, t7) => {
      const options = t7 === undefined ? {} : t7;
      if (!compressionPool.current) {
        throw new Error(t("error.processorNotReady"));
      }
      const sourceMimeType = detectMimeType(file);
      if (!sourceMimeType) {
        throw new Error(t("error.unsupportedFileType"));
      }
      const buffer = await file.arrayBuffer();
      const compressId = `${id}-full`;
      const compResult = await compressionPool.current.run({
        type: "compress",
        id: compressId,
        buffer,
        sourceMimeType,
        compressionMode,
        preserveDpi: true,
        minImageWidth: options.minImageWidth,
        minImageHeight: options.minImageHeight,
        profile,
        withThumbnail: Boolean(options.onPreview)
      } as CompressionRequest, options.onPreview && ((thumbnailBuffer, thumbnailContentType) => options.onPreview?.(new Blob([thumbnailBuffer], {
        type: thumbnailContentType
      }))));
      if (compResult.type === "compress-error") {
        throw new Error(compResult.error);
      }
      const contentType = compResult.contentType;
      const compressedBlob = new Blob([compResult.buffer], {
        type: contentType
      });
      return {
        contentType,
        compressedBlob,
        originalWidth: compResult.originalWidth,
        originalHeight: compResult.originalHeight,
        compressedWidth: compResult.width,
        compressedHeight: compResult.height,
        dpiX: compResult.dpiX,
        dpiY: compResult.dpiY
      };
    };
    $[4] = compressionMode;
    $[5] = profile;
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const compressImage = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = id_0 => {
      compressionPool.current?.cancel(`${id_0}-full`);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const cancelCompression = t7;
  let t8;
  if ($[9] !== compressImage) {
    t8 = {
      compressImage,
      cancelCompression
    };
    $[9] = compressImage;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}
function _temp() {
  return new Worker(new URL("../workers/compression.worker.ts", import.meta.url));
}